/**
 * Home Tests API (Spirometry & 6-Minute Walk Test)
 */

import { apiClient } from '../api-client';

// === TYPES ===

export interface SpirometryResult {
  id: number;
  patient_id: number;
  test_date: string;
  fev1_liters: number | null;
  fev1_percent_predicted: number | null;
  fvc_liters: number | null;
  fvc_percent_predicted: number | null;
  fev1_fvc_ratio: number | null;
  pef_liters_per_min: number | null;
  technique_quality: string | null;
  test_location: string | null;
  notes: string | null;
}

export interface SixMWTResult {
  id: number;
  patient_id: number;
  test_date: string;
  distance_meters: number | null;
  pre_test_spo2: number | null;
  pre_test_hr: number | null;
  post_test_spo2: number | null;
  post_test_hr: number | null;
  lowest_spo2: number | null;
  borg_score: number | null;
  test_location: string | null;
  environmental_conditions: string | null;
  notes: string | null;
}

export interface SpirometryCreate {
  patient_id: number;
  fev1_liters: number;
  fev1_percent_predicted: number;
  fvc_liters?: number;
  fvc_percent_predicted?: number;
  fev1_fvc_ratio?: number;
  pef_liters_per_min?: number;
  technique_quality?: 'good' | 'fair' | 'poor';
  test_location?: string;
  notes?: string;
}

export interface SixMWTCreate {
  patient_id: number;
  distance_meters: number;
  pre_test_spo2?: number;
  pre_test_hr?: number;
  post_test_spo2?: number;
  post_test_hr?: number;
  lowest_spo2?: number;
  borg_score?: number;
  test_location?: string;
  environmental_conditions?: string;
  notes?: string;
}

// === API FUNCTIONS ===

export const testsApi = {
  /**
   * Record a spirometry test
   */
  recordSpirometry: async (data: SpirometryCreate): Promise<{
    message: string;
    spirometry_id: number;
    test_date: string;
    fev1_percent_predicted: number;
  }> => {
    return apiClient.post('/tests/spirometry', data);
  },

  /**
   * Get spirometry test history for a patient
   */
  getSpirometryHistory: async (
    patientId: number,
    params?: { limit?: number; days?: number }
  ): Promise<{
    patient_id: number;
    results: SpirometryResult[];
    count: number;
  }> => {
    return apiClient.get(`/tests/spirometry/patient/${patientId}`, params);
  },

  /**
   * ✅ NEW: Get spirometry history for current patient (PATIENT role)
   */
  getMySpirometryHistory: async (
    params?: { limit?: number; days?: number }
  ): Promise<{
    patient_id: number;
    results: SpirometryResult[];
    count: number;
  }> => {
    return apiClient.get(`/tests/spirometry/me`, params);
  },

  /**
   * Record a 6-Minute Walk Test
   */
  recordSixMWT: async (data: SixMWTCreate): Promise<{
    message: string;
    test_id: number;
    test_date: string;
    distance_meters: number;
  }> => {
    return apiClient.post('/tests/six-mwt', data);
  },

  /**
   * Get 6MWT history for a patient
   */
  getSixMWTHistory: async (
    patientId: number,
    params?: { limit?: number; days?: number }
  ): Promise<{
    patient_id: number;
    results: SixMWTResult[];
    count: number;
  }> => {
    return apiClient.get(`/tests/six-mwt/patient/${patientId}`, params);
  },

  /**
   * ✅ NEW: Get 6MWT history for current patient (PATIENT role)
   */
  getMySixMWTHistory: async (
    params?: { limit?: number; days?: number }
  ): Promise<{
    patient_id: number;
    results: SixMWTResult[];
    count: number;
  }> => {
    return apiClient.get(`/tests/six-mwt/me`, params);
  },

  /**
   * Get combined test history (both Spirometry and 6MWT)
   */
  getAllTests: async (
    patientId: number,
    params?: { limit?: number; days?: number }
  ): Promise<{
    patient_id: number;
    period_days: number;
    spirometry: {
      count: number;
      results: SpirometryResult[];
    };
    six_mwt: {
      count: number;
      results: SixMWTResult[];
    };
    total_tests: number;
  }> => {
    return apiClient.get(`/tests/patient/${patientId}`, params);
  },

  /**
   * ✅ NEW: Get single spirometry test by ID
   */
  getSpirometryById: async (testId: number): Promise<SpirometryResult> => {
    return apiClient.get(`/tests/spirometry/${testId}`);
  },

  /**
   * ✅ NEW: Update spirometry test
   */
  updateSpirometry: async (testId: number, data: {
    fev1_liters?: number;
    fev1_percent_predicted?: number;
    fvc_liters?: number;
    fvc_percent_predicted?: number;
    fev1_fvc_ratio?: number;
    pef_liters_per_min?: number;
    technique_quality?: string;
    test_location?: string;
    notes?: string;
  }): Promise<{ status: string; message: string; spirometry_id: number }> => {
    return apiClient.put(`/tests/spirometry/${testId}`, data);
  },

  /**
   * ✅ NEW: Delete spirometry test
   */
  deleteSpirometry: async (testId: number): Promise<{ status: string; message: string; spirometry_id: number }> => {
    return apiClient.delete(`/tests/spirometry/${testId}`);
  },

  /**
   * ✅ NEW: Get single 6MWT test by ID
   */
  getSixMWTById: async (testId: number): Promise<SixMWTResult> => {
    return apiClient.get(`/tests/six-mwt/${testId}`);
  },

  /**
   * ✅ NEW: Update 6MWT test
   * ✅ FIX #17: Updated field names to match backend schema
   */
  updateSixMWT: async (testId: number, data: {
    distance_meters?: number;
    pre_spo2?: number;           // ✅ Fixed: was pre_test_spo2
    pre_heart_rate?: number;     // ✅ Fixed: was pre_test_hr
    post_spo2?: number;          // ✅ Fixed: was post_test_spo2
    post_heart_rate?: number;    // ✅ Fixed: was post_test_hr
    lowest_spo2?: number;
    dyspnea_score?: number;      // ✅ Fixed: was borg_score
    test_location?: string;
    notes?: string;
  }): Promise<{ status: string; message: string; test_id: number }> => {
    return apiClient.put(`/tests/six-mwt/${testId}`, data);
  },

  /**
   * ✅ NEW: Delete 6MWT test
   */
  deleteSixMWT: async (testId: number): Promise<{ status: string; message: string; test_id: number }> => {
    return apiClient.delete(`/tests/six-mwt/${testId}`);
  },

  /**
   * ✅ NEW: Get home test adherence for current patient
   * Calculates adherence based on prescribed schedules and completed tests
   */
  getMyHomeTestAdherence: async (days: number = 30): Promise<{
    adherence_rate: number;
    completed: number;
    scheduled: number;
    pending: number;
    missed: number;
    spirometry_completed: number;
    six_mwt_completed: number;
    spirometry_scheduled: number;
    six_mwt_scheduled: number;
    risk_level: string;
  }> => {
    return apiClient.get(`/tests/adherence/me`, { days });
  },
};

// Standalone exports for hooks
export const recordSpirometry = testsApi.recordSpirometry;
export const getSpirometryHistory = testsApi.getSpirometryHistory;
export const recordSixMWT = testsApi.recordSixMWT;
export const getSixMWTHistory = testsApi.getSixMWTHistory;
export const getAllTests = testsApi.getAllTests;

export const getSpirometryById = testsApi.getSpirometryById;
export const updateSpirometry = testsApi.updateSpirometry;
export const deleteSpirometry = testsApi.deleteSpirometry;

export const getSixMWTById = testsApi.getSixMWTById;
export const updateSixMWT = testsApi.updateSixMWT;
export const deleteSixMWT = testsApi.deleteSixMWT;